import { useEffect, useState } from "react";
import { STATUS } from "./harvestEntryConstants.js";
import { fetchConfigRecords, fetchActualYields } from "./harvestEntry.storage.js";

/**
 * Fetches Config Record and Actual Yield tables, then builds a merged
 * dataset (CR UNION AY) for the DataGrid.
 *
 * - Every block in CR gets pass 1 and pass 2 rows.
 * - If AY already has a matching row, its id / status / date / yield are used.
 * - If not, a synthetic row is emitted with id=null and status "planted".
 *   No rows are inserted into the DB on page load.
 */
export function useBroccoliHarvestEntryData({ siteName, organization }) {
  const [mergedRows, setMergedRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!siteName) return;

    const controller = new AbortController();

    const loadAndMerge = async () => {
      setLoading(true);
      setError(null);

      try {
        // 1. Fetch both tables in parallel
        const [configRecords, actualYields] = await Promise.all([
          fetchConfigRecords(siteName),
          fetchActualYields(siteName),
        ]);

        console.log("[HarvestEntry] configRecords:", configRecords);
        console.log("[HarvestEntry] actualYields:", actualYields);

        if (controller.signal.aborted) return;

        // 2. Build lookup: "${paddock}||${block}||${pass_number}" → AY row
        const yieldMap = new Map();
        for (const ay of actualYields) {
          const key = `${ay.paddock}||${ay.block}||${ay.pass_number}`;
          yieldMap.set(key, ay);
        }

        // Build max-pass lookup per block from AY (always at least 2)
        const maxPassMap = new Map();
        for (const ay of actualYields) {
          const blockKey = `${ay.paddock}||${ay.block}`;
          maxPassMap.set(blockKey, Math.max(maxPassMap.get(blockKey) ?? 2, ay.pass_number));
        }

        // 3. CR UNION AY — all stored passes (min 2) for each block
        const rows = [];
        for (const config of configRecords) {
          const blockKey = `${config.paddock}||${config.block}`;
          const maxPass = maxPassMap.get(blockKey) ?? 2;
          for (let pass = 1; pass <= maxPass; pass++) {
            const key = `${config.paddock}||${config.block}||${pass}`;
            const ay = yieldMap.get(key);

            rows.push({
              id: ay?.id ?? null,   // null for blocks not yet in AY
              _key: key,            // stable client-side key for rowKeyGetter
              paddock: config.paddock,
              block: config.block,
              pass,
              area_ha: config.area_ha ?? null,
              average_weight: config.average_weight ?? null,

              // Saved state from AY (or defaults for synthetic rows)
              status: ay?.status ?? STATUS.PLANTED,
              harvest_date: ay?.date_of_harvest ?? null,
              yield_kg: ay?.actual_yield != null ? ay.actual_yield / 1000 : null,

              // Pending edits — all null on initial load
              pendingStatus: null,
              pendingHarvestDate: null,
              pendingYield: null,
            });
          }
        }

        setMergedRows(rows);
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error("Error loading harvest entry data:", err);
          setError(err);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    loadAndMerge();
    return () => controller.abort();
  }, [siteName, organization]);

  return { mergedRows, loading, error };
}
